import { API_Submit_Request, API_Submit_Response, getMyCareStartedOn, deserializeEMR, EMRInfo, EMRInfoJSON, EMRKindID, EMRTypeID, FileID, GAHTBloodResults_ID, GAHTIntake_ID, GAHTTreatmentFeedback_ID, GAHTTreatmentFeedbackContent, MimeType, serializeUserServicesRequestContent, serializeUserSurgeryRequestContent, UserID, UserServicesRequestContent, UserSurgeryRequestContent } from "@imago/model"
import { makeEMRInfo } from "@imago/model/api/EMRsV1.ts"
import { QueryClient, useMutation, useQueryClient } from "@tanstack/react-query"
import { Temporal } from "@imago/api-client"
import { TF_FormID } from "@imago/api-client-typeform"
import { FatUserQuery } from "./user.ts"
import { api } from "./_imago_api.ts"
import { useConfig } from "../api.ts"
import { parallel } from "../../../backend/src/libs/parallel.ts"
import { useGAHTSetupAppointment } from '../components/GAHT/tasks/TalkWithAPhysician.tsx'

const EMRsQuery = {
	use() {
		return {data: FatUserQuery.use().data?.emrs?.toSorted((a, b) => Temporal.Instant.compare(a.timestamp, b.timestamp))}
	},
	async add(qc: QueryClient, result: EMRInfo) {
		await FatUserQuery.patchData(qc, [], old => ({
			...old!,
			emrs: [
				result,
				...old!.emrs || [],
			],
		}))
	},
	async fake(qc: QueryClient, kindID: EMRKindID) {
		await this.add(qc, makeEMRInfo({
			patientID: '' as UserID,
			fileID: 'dummy' as FileID,
			timestamp: Temporal.Now.instant(),
			kindID,
			mimeType: 'whatever' as MimeType,
		}))
	},
	async invalidate(qc: QueryClient, t: []) {
		await FatUserQuery.invalidate(qc, [])
	},
}

function useLatestEMRThat(predicate: (emr: EMRInfo) => boolean) {
	const emrs = EMRsQuery.use().data
	if (emrs === undefined) return
	return emrs.toReversed().filter(predicate)[0] ?? null
}

function useLatestEMROfType(types: Set<EMRTypeID>) {
	return useLatestEMRThat(emr => types.has(emr.type))
}

export function useWelcomed(): boolean | undefined {
	const lastWelcomed = useLatestEMRThat(emr => emr.hasWelcomed)
	if (lastWelcomed === undefined) return
	return lastWelcomed ? true : false
}

export function useLastGAHTBloodResults(): Temporal.Instant | null | undefined {
	const lastResults = useLatestEMROfType(new Set([GAHTBloodResults_ID]))
	if (lastResults === undefined) return
	return lastResults ? lastResults.timestamp : null
}

export function useLastGAHTTreatmentFeedback(): Temporal.Instant | null | undefined {
	const lastFeedback = useLatestEMROfType(new Set([GAHTTreatmentFeedback_ID]))
	if (lastFeedback === undefined) return
	return lastFeedback ? lastFeedback.timestamp : null
}

export function useGAHTIntaken(): boolean | undefined {
	const lastIntake = useLatestEMROfType(new Set([GAHTIntake_ID]))
	if (lastIntake === undefined) return
	return lastIntake ? true : false
}

export function useGAHTConsented(): Temporal.Instant | null | undefined {
	const myRecords = EMRsQuery.use().data

	const setupAppointment = useGAHTSetupAppointment()

	if (myRecords === undefined) return

	const myCareStartedOn = getMyCareStartedOn({myRecords})
	if (myCareStartedOn) return myCareStartedOn

	const setupAppointmentWasALongTimeAgo = setupAppointment ? Temporal.Instant.compare(setupAppointment.end.toInstant(), Temporal.Instant.from('2025-01-01T00:00:00+02:00')) < 0 : false

	// TODO XXX remove after we backfill consents
	if (setupAppointment && setupAppointmentWasALongTimeAgo)
		return setupAppointment.end.toInstant()

	return null
}

export function useSubmitDocuments() {
	const qc = useQueryClient()

	const mutation = useMutation({
		mutationFn: async (data: {
			gahtTreatmentFeedback?: GAHTTreatmentFeedbackContent,
			userServicesRequest?: UserServicesRequestContent,
			userSurgeryRequest?: UserSurgeryRequestContent,
		}) => {
			try {
				const result = await api.post('', {
					json: {
						gahtTreatmentFeedback: data.gahtTreatmentFeedback,
						userServicesRequest: serializeUserServicesRequestContent(data.userServicesRequest)!,
						userSurgeryRequest: serializeUserSurgeryRequestContent(data.userSurgeryRequest)!,
					} satisfies API_Submit_Request,
				}).json() as API_Submit_Response

				await parallel(result.map(emr => EMRsQuery.add(qc, deserializeEMR(emr))))
			} catch (e) {
				EMRsQuery.invalidate(qc, [])
				throw e
			}
		}
	})
	return mutation
}

export function useTfFile_internal() {
	const qc = useQueryClient()

	const mutation = useMutation({
		mutationFn: async ({form, response}: {form: TF_FormID, response: string}) => {
			try {
				const emr = deserializeEMR(await api.post('typeform/', {
					json: {
						form,
						response,
					},
				}).json() as EMRInfoJSON)

				await EMRsQuery.add(qc, emr)
			} catch (e) {
				EMRsQuery.invalidate(qc, [])
				throw e
			}
		}
	})

	return mutation
}

export function useSubmitGAHTBloodResults() {
	const qc = useQueryClient()

	const mutation = useMutation({
		mutationFn: async ({files}: {
			files: File[]
		}) => {
			try {
				const data = new FormData()
				let i = 0
				for (const file of files) {
					data.append(`file${i}`, file, file.name)
					i += 1
				}
				const result = deserializeEMR(await api.post('gaht/blood-results/', {
					body: data,
				}).json())

				EMRsQuery.add(qc, result)
			} catch (e) {
				EMRsQuery.invalidate(qc, [])
				throw e
			}
		}
	})
	return mutation
}

export function useFakeSubmitGAHTBloodResults() {
	const config = useConfig()
	const qc = useQueryClient()
	const mutation = {
		mutateAsync: async () => await EMRsQuery.fake(qc, GAHTBloodResults_ID),
		isPending: false,
	}
	return config.testMode ? mutation : null
}

export function useTESTSetWelcomed() {
	const config = useConfig()
	const qc = useQueryClient()

	const mutation = useMutation({
		mutationFn: async () => {
			try {
				const result = deserializeEMR(await api.put('TEST/welcomed/').json())

				EMRsQuery.add(qc, result)
			} catch (e) {
				EMRsQuery.invalidate(qc, [])
				throw e
			}
		}
	})
	return config.testMode ? mutation : null
}
